/**
 * ARCADIA '84: REMASTERED — shell entry.
 *
 * The cabinet no longer runs cartridges in-process; each game is a
 * standalone app served under /games/<id>/ and hosted in an iframe.
 * The shell owns: the gallery, launch/teardown, the score pipeline
 * (initials → POST /scores → rank toast), auth, PWA install, and
 * analytics. Games own everything inside the frame.
 *
 * Boot order:
 * 1. Analytics + auth callback + session restore (unchanged from '84).
 * 2. Install funnel + orientation overlay (unchanged).
 * 3. Mute controls (cabinet-level flag broadcast over the game bridge).
 * 4. Render the gallery grid. Player inserts a coin; play.ts takes over.
 */

import './styles/tokens.css';
import './styles/shell.css';
import './styles/remastered.css';
import { runCallbackIfNeeded } from './auth/callback.js';
import { runBillingReturnIfNeeded } from './billing/return.js';
import { wireFooterSession } from './auth/footer-session.js';
import { getSession } from '@arcraz/arcadia-auth-web';
import { mountCabinetAd } from './cabinet/cabinet-ad.js';
import { wireFooterCredits } from './cabinet/footer-credit.js';
import { relaunchPending, renderGrid } from './cabinet/grid.js';
import { refreshGlobalPlays } from './cabinet/plays.js';
import { wireMuteControls } from './cabinet/mute.js';
import { identify, initAnalytics, track } from './runtime/analytics.js';
import {
  createInstallController,
  type InstallController,
  type InstallStatus,
} from './runtime/install.js';
import { attachOrientationController } from './runtime/orientation.js';
import { attachUpdateController } from './runtime/update.js';

function isTouch(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
}

function wireInstallFunnel(controller: InstallController): void {
  const banner = document.getElementById('install-banner');
  const acceptBtn = document.getElementById('install-banner-accept');
  const dismissBtn = document.getElementById('install-banner-dismiss');
  const overlay = document.getElementById('install-overlay');
  const overlayDismiss = document.getElementById('install-overlay-dismiss');

  if (
    !(banner instanceof HTMLElement) ||
    !(acceptBtn instanceof HTMLButtonElement) ||
    !(dismissBtn instanceof HTMLButtonElement) ||
    !(overlay instanceof HTMLElement) ||
    !(overlayDismiss instanceof HTMLButtonElement)
  ) {
    console.warn('[arcadia] install funnel: DOM nodes missing — skipping');
    return;
  }

  const seenSurfaces = new Set<string>();

  const render = (status: InstallStatus): void => {
    // Touch devices only — installing a landscape cabinet PWA on
    // desktop is noise (same gate as the '84 cabinet).
    const showBanner = status.surface === 'banner' && isTouch();
    const showOverlay = status.surface === 'ios-instructions' && isTouch();
    banner.hidden = !showBanner;
    overlay.hidden = !showOverlay;

    if (showBanner && !seenSurfaces.has('banner')) {
      seenSurfaces.add('banner');
      track('install_banner_shown', { surface: 'banner' });
    }
    if (showOverlay && !seenSurfaces.has('ios-instructions')) {
      seenSurfaces.add('ios-instructions');
      track('install_banner_shown', { surface: 'ios-instructions' });
    }
  };

  acceptBtn.addEventListener('click', () => {
    track('install_accepted', { surface: 'banner' });
    void controller.install().then((outcome) => {
      track('install_outcome', { outcome });
    });
  });
  dismissBtn.addEventListener('click', () => {
    track('install_dismissed', { surface: 'banner' });
    controller.dismiss();
  });
  overlayDismiss.addEventListener('click', () => {
    track('install_dismissed', { surface: 'ios-instructions' });
    controller.dismiss();
  });

  render(controller.getStatus());
  controller.subscribe(render);
}

async function boot(): Promise<void> {
  initAnalytics();

  const callback = await runCallbackIfNeeded();
  if (callback.handled) {
    track('auth_callback_handled', { signedIn: callback.signedIn });
  }
  if (callback.handled && !callback.signedIn) {
    return; // callback handler rendered its own error card
  }

  // PayPal return/cancel (sc-6943) — finalize a purchase before the gallery
  // renders, so the pending cartridge relaunches straight into unlimited play.
  const billing = await runBillingReturnIfNeeded();
  if (billing.handled) {
    track('billing_return_handled', { entitled: billing.entitled });
  }

  const session = getSession();
  if (session) {
    identify(session.userId, { email: session.email });
    if (callback.signedIn) {
      track('auth_signin_completed', { userId: session.userId });
    }
  }

  const installController = createInstallController();
  attachOrientationController();
  attachUpdateController();
  wireMuteControls();
  wireInstallFunnel(installController);
  wireFooterSession();
  wireFooterCredits();

  document.body.dataset.mode = 'attract';
  renderGrid();
  mountCabinetAd();

  // After a sign-in round-trip, drop the player back into the game they
  // were trying to coin before the redirect (sc-6940). Same relaunch after
  // a completed purchase (sc-6943): the pending cartridge was stashed
  // before the PayPal redirect and now plays unlimited.
  if (callback.signedIn || billing.entitled) relaunchPending();

  // Global most-played arrives async; re-rank the gallery when it lands.
  void refreshGlobalPlays().then((fresh) => {
    if (fresh) renderGrid();
  });

  console.info('[arcadia] REMASTERED cabinet booted', {
    install: installController.getStatus().surface,
  });
}

void boot();

export {};
